<script lang="ts">
    import { formatDate } from '$lib/utils/dateUtils';
    import { formatNumber } from '$lib/utils/numberUtils';
    import { t } from '$lib/i18n';
    import { goto } from '$app/navigation';
    import { Heart, Calendar } from 'lucide-svelte';
    import { Button } from "$lib/components/shadcn/ui/button";
    import { Card, CardContent } from "$lib/components/shadcn/ui/card";
    import ShareButton from '$lib/components/ShareButton.svelte';

    export let contentContainer: HTMLElement;
    export let currentMode: string;
    export let videoDetails: any;
    export let channelData: any;
    export let chatUrl: string;
    export let browser: boolean;
    export let visibleParagraphs: string[];
    export let paragraphs: string[];
    export let MAX_VISIBLE_PARAGRAPHS: number;
    export let isDescriptionExpanded: boolean;
    export let isFavorite: boolean;
    export let relatedVideos: any[];
    
    // モードの定数
    const MODES = {
        CHAT: 'chat',
        DETAILS: 'details',
        RECENT: 'recent'
    };
    
    // テキスト内のURLをリンクに変換
    function linkify(text: string): string {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        return text ? text.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-400 hover:underline">$1</a>') : '';
    }
    
    // 説明文の表示/非表示を切り替え
    function toggleDescription() {
        isDescriptionExpanded = !isDescriptionExpanded;
    }
    
    // 関連動画をクリックしたときの処理
    function handleRelatedVideoClick(videoId: string) {
        if (!browser) return;
        goto(`/video/${videoId}`);
    }
    
    // お気に入りボタンの処理関数
    function handleFavoriteClick(event: MouseEvent) {
        event.stopPropagation();
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('favoriteClick', { detail: { videoId: videoDetails.id } }));
        }
    }
</script>

<div bind:this={contentContainer} class="flex-grow overflow-y-auto content-area">
    {#if currentMode === MODES.CHAT}
        {#if browser && videoDetails.live_broadcast_content === 'live'}
            {#if videoDetails.platform === 'youtube' && chatUrl}
                <iframe
                    src={chatUrl}
                    title="YouTube Live Chat"
                    frameborder="0"
                    allowfullscreen
                    class="w-full h-full"
                ></iframe>
            {:else if videoDetails.platform === 'twitch' && channelData?.twitch_id}
                <iframe
                    src={chatUrl}
                    title="Twitch Chat"
                    frameborder="0"
                    scrolling="no"
                    class="w-full h-full"
                ></iframe>
            {/if}
        {:else}
            <p class="p-4 text-center text-gray-600 dark:text-gray-400">このビデオにはライブチャットがありません。</p>
        {/if}
    {:else if currentMode === MODES.DETAILS}
        <!-- モバイル詳細セクション -->
        <div class="p-4 space-y-4 w-full">
            <!-- タイトルをCardTitleとして表示 -->
            <h1 class="text-base font-medium text-foreground break-words w-full">
                {videoDetails.title}
            </h1>

            <!-- 日付情報とボタン類を横並びに -->
            <div class="flex items-center justify-between flex-wrap gap-2 w-full">
                <div class="flex items-center gap-2 text-[13px] text-muted-foreground">
                    <Calendar size={14} />
                    <time datetime={videoDetails.published_at}>
                        {formatDate(videoDetails.published_at)}
                    </time>
                </div>
                
                <!-- ボタン類のコンテナ -->
                <div class="flex items-center gap-1.5">
                    <!-- お気に入りボタン -->
                    <Button
                        variant={isFavorite ? "default" : "ghost"}
                        size="sm"
                        on:click={handleFavoriteClick}
                        class="h-8"
                    >
                        <Heart
                            class="w-4 h-4 mr-1.5"
                            fill={isFavorite ? '#ef4444' : 'none'}
                            color={isFavorite ? '#ef4444' : 'currentColor'}
                        />
                        <span class="text-xs">{isFavorite ? 'お気に入り解除' : 'お気に入り'}</span>
                    </Button>

                    <!-- シェアボタン -->
                    <ShareButton 
                        url={`/video/${videoDetails.id}`}
                        title={videoDetails.title}
                    />
                </div>
            </div>

            <!-- チャンネル情報 -->
            <Card>
                <CardContent class="p-3">
                    <a
                        href="/channel/{videoDetails.channel_id}"
                        class="flex items-center gap-3 hover:bg-accent/50 dark:hover:bg-zinc-800/80 rounded-lg p-2 -ml-2 transition-colors"
                    >
                        <div class="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 ring-1 ring-border">
                            <img
                                src={channelData?.thumbnail}
                                alt={videoDetails.channel_title}
                                class="w-full h-full object-cover"
                            />
                        </div>
                        <div class="flex flex-col">
                            <span class="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                                {videoDetails.channel_title}
                            </span>
                            <span class="text-xs text-muted-foreground">
                                {channelData?.subscriber_count ? `${formatNumber(channelData.subscriber_count)}人のチャンネル登録者` : ''}
                            </span>
                        </div>
                    </a>
                </CardContent>
            </Card>

            <!-- 説明文 -->
            <div class="text-[15px] text-foreground/90 space-y-4 w-full overflow-hidden">
                {#each visibleParagraphs as paragraph}
                    <p class="whitespace-pre-wrap leading-relaxed break-words">
                        {@html linkify(paragraph)}
                    </p>
                {/each}
                
                {#if paragraphs.length > MAX_VISIBLE_PARAGRAPHS}
                    <Button
                        variant="ghost"
                        size="sm"
                        on:click={toggleDescription}
                        class="p-0 h-auto text-muted-foreground hover:text-foreground"
                    >
                        {isDescriptionExpanded ? '一部を表示' : 'もっと見る'}
                    </Button>
                {/if}
            </div>
        </div>
    {:else if currentMode === MODES.RECENT}
        <!-- モバイル新着動画セクション -->
        <div class="p-4 space-y-4">
            <h2 class="text-lg font-bold text-foreground mb-3">新着動画</h2>
            <div class="space-y-3">
                {#each relatedVideos as video}
                    <Card class="overflow-hidden hover:shadow-md transition-shadow">
                    <button 
                            class="w-full"
                            on:click={() => handleRelatedVideoClick(video.id)}
                        >
                            <div class="flex">
                                <!-- サムネイル -->
                                <div class="relative flex-shrink-0 w-32">
                                    <div class="relative">
                                        <img 
                                            src={video.thumbnail_url} 
                                            alt={video.title}
                                            class="w-full h-full object-cover"
                                        />
                                    </div>
                                </div>

                                <!-- 動画情報 -->
                                <CardContent class="flex-1 min-w-0 text-left p-3">
                                    <h3 class="text-[15px] font-medium text-foreground line-clamp-2 leading-snug mb-2">
                                        {video.title}
                                    </h3>
                                    <time class="text-[13px] text-muted-foreground" datetime={video.published_at}>
                                        {formatDate(video.published_at)}
                                    </time>
                                </CardContent>
                            </div>
                        </button>
                    </Card>
                {/each}
            </div>
        </div>
    {/if}
</div> 